import {
  Box,
  Button,
  Card,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { awsImageRootUrl } from "lib/constants";
import { Item } from "lib/features/item/types";
import {
  selectCustomerDetails,
  selectIsExistingBookingLink,
} from "lib/features/message/messageSlice";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import deleteIcon from "../../../assets/icons/bi_trash3.png";
import clock from "../../../assets/icons/clock_light.svg";
import commission from "../../../assets/icons/Commiss.png";
import duplicate from "../../../assets/icons/ion_duplicate-outline.png";
import members from "../../../assets/icons/light_member.svg";
import edit from "../../../assets/icons/lucide_edit.png";
import ticket from "../../../assets/icons/ticket.svg";

interface RefereeItemCardProps {
  item: Item;
  pressDelete: (item: Item) => void;
  pressDuplicte: (item: Item) => void;
}
const RefereeItemListCard = ({
  item,
  pressDelete,
  pressDuplicte,
}: RefereeItemCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width: 376px)");

  const customerDetails = useSelector(selectCustomerDetails);
  const isExistingBookingLink = useSelector(selectIsExistingBookingLink);

  const showMaxGuest = useMemo(() => {
    const details = item?.itemDetails?.[0];
    if (!details) return null;
    const maxGuest = details.max_guest ?? 0;

    return maxGuest;
  }, [item?.itemDetails]);
  console.log("Rendering Item Card:", item.id);
  console.log("Item Details:", item.itemDetails);
  console.log("Availabilities:", item.availabilities);

  return (
    <Card
      key={item.id}
      sx={{
        marginTop: "1rem",
        fontFamily: theme.typography.fontFamily,
        borderRadius: "10px",
        width: "620px",
        position: { xs: "unset", sm: "relative" },
        height: "152px",
        boxShadow: "2px 4px 4px 0px rgba(0, 0, 0, 0.25)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "16px"
        }}
      >
        {/* Image Container */}
        <Box sx={{ width: "271px", height: "100%" }}>
          <img
            src={`${awsImageRootUrl}/${item.itemImages[0]?.uri}`}
            alt={"name"}
            style={{
              width: "100%",
              height: "100%",
              marginRight: "1rem",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              objectFit: "cover",
            }}
          />
        </Box>

        <Box
          sx={{
            width: "333px",
            height: "150px",
            display: "flex",
            flexDirection: "column",
            pt: "8px",
            pb: "16px"
          }}
        >
          {/* Details Container */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "333px",
              gap: "5px",
            }}
          >
            <Typography sx={{ fontSize: "22px", fontWeight: "600", lineHeight: "30px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {item.itemDetails[0]?.item_name}
            </Typography>

            {/* Info Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "20px",
              }}
            >
              {/* Ticket Price */}
              {item?.itemDetails[0]?.price && (
                <Typography
                  sx={{
                    fontSize: "15px",
                    color: "#000000",
                    display: "flex",
                    fontWeight: 400,
                    height: "23px",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <img
                    src={ticket}
                    alt="ticket icon"
                    style={{ width: "14px", height: "14px" }}
                  />
                  ${item?.itemDetails[0]?.price}
                </Typography>
              )}

              {/* Capacity */}
              {item?.itemDetails[0]?.max_guest && (
                <Typography
                  sx={{
                    fontSize: "15px",
                    color: "#000000",
                    display: "flex",
                    fontWeight: 400,
                    height: "23px",
                    alignItems: "center",
                    gap: "5px",
                    lineHeight: "22.5px",
                  }}
                >
                  <img
                    src={members}
                    alt="members icon"
                    style={{ width: "20px", height: "20px" }}
                  />
                  {showMaxGuest} {' '} max
                </Typography>
              )}

              {/* Duration */}
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#000000",
                  fontWeight: 400,
                  height: "23px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <img
                  src={clock}
                  alt="clock icon"
                  style={{ width: "16px", height: "16px" }}
                />
                {item?.itemDetails[0]?.duration
                  ? (() => {
                    const [hours, minutes] = (item.itemDetails[0].duration || '0:0').split(':');
                    const total = (Number(hours) || 0) + (Number(minutes) || 0) / 60;
                    return total.toFixed(1).replace('.0', '');
                  })()
                  : '-'} {' '}
                hr
              </Typography>

              {/*Commission*/}
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#000000",
                  fontWeight: 400,
                  height: "23px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <img
                  src={commission}
                  alt="comission icon"
                  style={{ width: "16px", height: "16px" }}
                />
                {item?.itemDetails[0]?.comission
                  ? item?.itemDetails[0]?.comission
                  : '-'}%
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: "600",
                color: "#286C2A",
                paddingY: "5px",
              }}
            >
              {item?.availabilities?.filter((f) => f.start_time && new Date(f.start_time) > new Date() && !f.private && f.record_status).length} {' '}Availabilities
              <span style={{ fontSize: "15px", fontWeight: 400, color: "#000000" }}> / {item?.availabilities?.filter((f) => f.start_time && new Date(f.start_time) > new Date() && f.private && f.record_status).length} {' '}Direct Links</span>
            </Typography>
          </Box>

          {/* Buttons Sections */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "315px",
              height: "31px",
              gap: "21px"
            }}
          >
            {!isExistingBookingLink && (
              <Button
                sx={{
                  minWidth: "24px",
                  width: "24px",
                  height: "24px",
                  p: 0,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#FFF",
                  },
                }}
                onClick={() => pressDuplicte(item)}
              >
                <img src={duplicate} alt="duplicate" />
              </Button>
            )}
            {!isExistingBookingLink && (
              <Button
                sx={{
                  minWidth: "24px",
                  width: "24px",
                  height: "24px",
                  p: 0,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#FFF",
                  },
                }}
                onClick={() => navigate(`/edit/item/${item?.id}`)}
              >
                <img src={edit} alt="edit" />
              </Button>
            )}
            {!isExistingBookingLink && (
              <Button
                sx={{
                  minWidth: "24px",
                  width: "24px",
                  height: "24px",
                  p: 0,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#FFF",
                  },
                }}
                onClick={() => pressDelete(item)}
              >
                <img src={deleteIcon} alt="delete" />
              </Button>
            )}
            <Button
              sx={{
                width: "180px",
                height: "31px",
                backgroundColor: "#fff",
                color: "#328734",
                fontSize: "15px",
                fontWeight: "400",
                paddingY: "8px",
                borderRadius: "8px",
                border: "1px solid #328734",
              }}
              onClick={() => navigate(`/availability/item/${item?.id}`)}
            >
              {customerDetails?.customer_name && isExistingBookingLink
                ? "Create Availability and Share"
                : "+ Add Availabilities"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default RefereeItemListCard;
